import { fx_crearBaseLocal, reqToPromise, txDone } from "../../../../utils/idbUtils";

const DB_NAME = "grpc_ventas_animales";
const DB_VERSION = 3;
const STORE_VENTAS = "ventas";
const STORE_SYNC = "sync";

/** Item mínimo que necesita el merge de delta (watermark por finca). */
export type GrpcVentaAnimalesItem = {
  id: string;
  eliminado: boolean;
  ultimaActualizacion: bigint;
  fincaId: string;
};

/** IndexedDB **nueva y separada** para las ventas de animales que trae
 *  `VentaAnimalesGrpcService` — mismo patrón que `GrpcCompraAnimalesDatabase`:
 *  delta real por `fincaId`. */
class GrpcVentaAnimalesDatabase {
  /** Conexión a la base local con el ayudante común, que al cambiar de
   *  versión conserva los datos (ver `fx_crearBaseLocal`). La versión 3
   *  (2026-09-30) se subió a pedido del usuario para una limpieza única al
   *  estrenar el ayudante: un navegador con una versión anterior vacía la
   *  base una sola vez y la vuelve a llenar desde el servidor. */
  private readonly _base = fx_crearBaseLocal(
    DB_NAME,
    DB_VERSION,
    [
      { nombre: STORE_VENTAS, clave: "id", indices: ["fincaId"] },
      { nombre: STORE_SYNC, clave: "fincaId" },
    ],
    { vaciarAntesDe: 3 },
  );

  /** Conexión a la base local, abierta la primera vez y reusada después. */
  private open(): Promise<IDBDatabase> {
    return this._base.abrir();
  }

  async getUltimaSincronizacionAsync(fincaId: string): Promise<bigint> {
    const db = await this.open();
    const store = db.transaction(STORE_SYNC, "readonly").objectStore(STORE_SYNC);
    const row = await reqToPromise<{ fincaId: string; ultimaSincronizacion: string } | undefined>(
      store.get(fincaId),
    );
    return row ? BigInt(row.ultimaSincronizacion) : 0n;
  }

  async putDeltaAsync<T extends GrpcVentaAnimalesItem>(
    fincaId: string,
    items: readonly T[],
    nuevoWatermark: bigint,
  ): Promise<void> {
    const db = await this.open();
    const tx = db.transaction([STORE_VENTAS, STORE_SYNC], "readwrite");
    const store = tx.objectStore(STORE_VENTAS);
    const syncStore = tx.objectStore(STORE_SYNC);

    for (const item of items) {
      if (item.eliminado) {
        store.delete(item.id);
      } else {
        store.put(item);
      }
    }
    syncStore.put({ fincaId, ultimaSincronizacion: nuevoWatermark.toString() });

    await txDone(tx);
  }

  async getListByFincaIdAsync<T>(fincaId: string): Promise<T[]> {
    const db = await this.open();
    const index = db
      .transaction(STORE_VENTAS, "readonly")
      .objectStore(STORE_VENTAS)
      .index("fincaId");
    return reqToPromise<T[]>(index.getAll(fincaId));
  }

  async putOneAsync<T extends GrpcVentaAnimalesItem>(item: T): Promise<void> {
    const db = await this.open();
    const tx = db.transaction(STORE_VENTAS, "readwrite");
    tx.objectStore(STORE_VENTAS).put(item);
    await txDone(tx);
  }

  async deleteOneAsync(id: string): Promise<void> {
    const db = await this.open();
    const tx = db.transaction(STORE_VENTAS, "readwrite");
    tx.objectStore(STORE_VENTAS).delete(id);
    await txDone(tx);
  }

  /** Actualiza (o inserta) varias ventas en una sola transacción, sin tocar
   *  el resto ni el watermark; las marcadas `eliminado` se borran. Lo usa
   *  `fx_aplicarCambiosMovimientoAsync` con las ventas renumeradas al
   *  eliminar una venta (2026-10-08). */
  async putManyAsync<T extends GrpcVentaAnimalesItem>(items: readonly T[]): Promise<void> {
    if (items.length === 0) return;
    const db = await this.open();
    const tx = db.transaction(STORE_VENTAS, "readwrite");
    const store = tx.objectStore(STORE_VENTAS);
    for (const item of items) {
      if (item.eliminado) {
        store.delete(item.id);
      } else {
        store.put(item);
      }
    }
    await txDone(tx);
  }
}

/** Singleton de la base de ventas de animales por gRPC-Web (con delta por
 *  finca). */
export const grpcVentaAnimalesDatabase = new GrpcVentaAnimalesDatabase();
